/**
 * Invitations List Component
 *
 * Displays all user invitations with status, actions to revoke/resend.
 */

import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../lib/api';

interface Invitation {
  invitationId: string;
  email: string;
  displayName?: string;
  invitedByUserId: string;
  roleIds?: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  expiresAt: string;
  acceptedAt?: string;
  revokedAt?: string;
  reminderCount: number;
  createdAt: string;
  updatedAt: string;
}

interface InvitationsListProps {
  onInvitationChange?: () => void;
}

export function InvitationsList({ onInvitationChange }: InvitationsListProps) {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const loadInvitations = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiRequest('GET', '/api/invitations');
      if (response.ok) {
        const data = await response.json();
        setInvitations(data.invitations || []);
      } else {
        const err = await response.json();
        setError(err.message || 'Failed to load invitations');
      }
    } catch (err: any) {
      setError('Failed to load invitations');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInvitations();
  }, []);

  const handleRevoke = async (invitationId: string) => {
    if (!confirm('Are you sure you want to revoke this invitation?')) return;

    try {
      setActionInProgress(invitationId);
      const response = await apiRequest('POST', `/api/invitations/${invitationId}/revoke`);
      if (response.ok) {
        await loadInvitations();
        onInvitationChange?.();
      } else {
        const err = await response.json();
        alert(err.message || 'Failed to revoke invitation');
      }
    } catch (err) {
      alert('Failed to revoke invitation');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleResend = async (invitationId: string) => {
    try {
      setActionInProgress(invitationId);
      const response = await apiRequest('POST', `/api/invitations/${invitationId}/resend`);
      if (response.ok) {
        await loadInvitations();
        alert('Invitation email resent');
      } else {
        const err = await response.json();
        alert(err.message || 'Failed to resend invitation');
      }
    } catch (err) {
      alert('Failed to resend invitation');
    } finally {
      setActionInProgress(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      expired: 'bg-gray-100 text-gray-800',
      revoked: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  if (isLoading) {
    return (
      <div className="bg-card shadow rounded-lg p-6">
        <div className="text-center text-muted-foreground">Loading invitations...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card shadow rounded-lg p-6">
        <div className="text-center text-red-600">{error}</div>
        <div className="text-center mt-2">
          <button onClick={loadInvitations} className="text-indigo-600 hover:text-indigo-800">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card shadow rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Invitations</h2>
          <button
            onClick={loadInvitations}
            className="text-sm text-indigo-600 hover:text-indigo-800"
          >
            Refresh
          </button>
        </div>

        {invitations.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">No invitations found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Invitee
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Expires
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Reminders
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {invitations.map((invitation) => (
                  <tr key={invitation.invitationId}>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {invitation.displayName || invitation.email}
                        </div>
                        {invitation.displayName && (
                          <div className="text-sm text-muted-foreground">{invitation.email}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getStatusBadge(invitation.status)}
                      {invitation.status === 'pending' && isExpired(invitation.expiresAt) && (
                        <span className="ml-2 text-xs text-red-600">(Expired)</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {formatDate(invitation.createdAt)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {formatDate(invitation.expiresAt)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {invitation.reminderCount}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {invitation.status === 'pending' && (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleResend(invitation.invitationId)}
                            disabled={actionInProgress === invitation.invitationId}
                            className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
                          >
                            Resend
                          </button>
                          <button
                            onClick={() => handleRevoke(invitation.invitationId)}
                            disabled={actionInProgress === invitation.invitationId}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          >
                            Revoke
                          </button>
                        </div>
                      )}
                      {invitation.status === 'accepted' && (
                        <span className="text-green-600">
                          Accepted {invitation.acceptedAt && formatDate(invitation.acceptedAt)}
                        </span>
                      )}
                      {invitation.status === 'revoked' && (
                        <span className="text-muted-foreground">
                          Revoked {invitation.revokedAt && formatDate(invitation.revokedAt)}
                        </span>
                      )}
                      {invitation.status === 'expired' && (
                        <span className="text-muted-foreground">Expired</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
